import React, { useState, useEffect, useRef } from 'react'
import styles from './MoviePlayerModal.module.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlay,
  faPause,
  faVolumeUp,
  faVolumeMute,
  faExpand,
  faCompress,
  faChevronLeft,
  faChevronRight,
  faTimes,
  faSpinner
} from '@fortawesome/free-solid-svg-icons'
import ImageWithFallback from '../ImageWithFallback/ImageWithFallback'

const MoviePlayerModal = ({ movie, episodes, initialEpisode, onClose, onEpisodeChange }) => {
  const videoRef = useRef(null)
  const containerRef = useRef(null)
  
  const [currentEpisode, setCurrentEpisode] = useState(initialEpisode || episodes[0] || null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [videoLoading, setVideoLoading] = useState(true)
  const [showControls, setShowControls] = useState(true)
  const [mouseIdleTimer, setMouseIdleTimer] = useState(null)

  const isMovie = movie?.type === 'movie' || !episodes || episodes.length === 0
  const isSeries = movie?.type === 'series' && episodes && episodes.length > 0

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleLoadStart = () => setVideoLoading(true)
    const handleCanPlay = () => setVideoLoading(false)
    const handleTimeUpdate = () => setCurrentTime(video.currentTime)
    const handleDurationChange = () => setDuration(video.duration)
    const handleVolumeChange = () => {
      setVolume(video.volume)
      setIsMuted(video.muted)
    }

    video.addEventListener('loadstart', handleLoadStart)
    video.addEventListener('canplay', handleCanPlay)
    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('durationchange', handleDurationChange)
    video.addEventListener('volumechange', handleVolumeChange)

    return () => {
      video.removeEventListener('loadstart', handleLoadStart)
      video.removeEventListener('canplay', handleCanPlay)
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('durationchange', handleDurationChange)
      video.removeEventListener('volumechange', handleVolumeChange)
    }
  }, [currentEpisode])

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  const togglePlayPause = () => {
    const video = videoRef.current
    if (video.paused) {
      video.play()
      setIsPlaying(true)
    } else {
      video.pause()
      setIsPlaying(false)
    }
  }

  const handleVolumeChange = (newVolume) => {
    const video = videoRef.current
    video.volume = newVolume
    setVolume(newVolume)
    if (newVolume === 0) {
      video.muted = true
    } else if (video.muted) {
      video.muted = false
    }
  }

  const toggleMute = () => {
    const video = videoRef.current
    video.muted = !video.muted
  }

  const handleSeek = (e) => {
    const video = videoRef.current
    const progressBar = e.currentTarget
    const rect = progressBar.getBoundingClientRect()
    const percent = (e.clientX - rect.left) / rect.width
    video.currentTime = percent * video.duration
  }

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await containerRef.current.requestFullscreen()
    } else {
      await document.exitFullscreen()
    }
  }

  const handleEpisodeSelect = (episode) => {
    setCurrentEpisode(episode)
    setCurrentTime(0)
    if (onEpisodeChange) {
      onEpisodeChange(episode)
    }
    if (videoRef.current) {
      videoRef.current.currentTime = 0
      videoRef.current.play()
      setIsPlaying(true)
    }
  }

  const handleNextEpisode = () => {
    if (!isSeries) return
    const currentIndex = episodes.findIndex(ep => ep.id === currentEpisode.id)
    if (currentIndex < episodes.length - 1) {
      handleEpisodeSelect(episodes[currentIndex + 1])
    }
  }

  const handlePrevEpisode = () => {
    if (!isSeries) return
    const currentIndex = episodes.findIndex(ep => ep.id === currentEpisode.id)
    if (currentIndex > 0) {
      handleEpisodeSelect(episodes[currentIndex - 1])
    }
  }

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00'
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const handleMouseMove = () => {
    setShowControls(true)
    if (mouseIdleTimer) clearTimeout(mouseIdleTimer)
    const timer = setTimeout(() => {
      if (isPlaying && !isFullscreen) setShowControls(false)
    }, 3000)
    setMouseIdleTimer(timer)
  }

  const getVideoSource = () => {
    if (isMovie && movie) {
      return movie.video_file || movie.video_url || null
    }
    if (isSeries && currentEpisode) {
      return currentEpisode.video_file || currentEpisode.video_url || null
    }
    return null
  }

  const videoSource = getVideoSource()
  const currentIndex = isSeries && currentEpisode ? episodes.findIndex(ep => ep.id === currentEpisode.id) : -1

  // Группировка эпизодов по сезонам
  const episodesBySeason = isSeries ? episodes.reduce((acc, episode) => {
    const season = episode.season_number || 1
    if (!acc[season]) acc[season] = []
    acc[season].push(episode)
    return acc
  }, {}) : {}

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div 
        className={`${styles.modalContent} ${isFullscreen ? styles.fullscreen : ''}`} 
        onClick={(e) => e.stopPropagation()}
        ref={containerRef}
      >
        <button className={styles.closeButton} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <div className={styles.playerSection} onMouseMove={handleMouseMove}>
          {videoSource ? (
            <>
              <video
                ref={videoRef}
                className={styles.videoPlayer}
                src={videoSource}
                poster={currentEpisode?.thumbnail || movie?.poster}
                onClick={togglePlayPause}
              />

              {videoLoading && (
                <div className={styles.videoLoading}>
                  <FontAwesomeIcon icon={faSpinner} className={styles.spinner} spin />
                </div>
              )}

              {!isPlaying && !videoLoading && (
                <div className={styles.centerPlayButton} onClick={togglePlayPause}>
                  <FontAwesomeIcon icon={faPlay} />
                </div>
              )}

              <div className={`${styles.controls} ${showControls ? styles.visible : styles.hidden}`}>
                <div className={styles.progressContainer}>
                  <div className={styles.progressBar} onClick={handleSeek}>
                    <div 
                      className={styles.progressFilled}
                      style={{ width: `${(currentTime / duration) * 100}%` }}
                    />
                  </div>
                </div>

                <div className={styles.controlsBar}>
                  <div className={styles.controlsLeft}>
                    <button className={styles.controlButton} onClick={togglePlayPause}>
                      <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                    </button>

                    {isSeries && (
                      <>
                        <button 
                          className={styles.controlButton}
                          onClick={handlePrevEpisode}
                          disabled={currentIndex === 0}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>

                        <button 
                          className={styles.controlButton}
                          onClick={handleNextEpisode}
                          disabled={currentIndex === episodes.length - 1}
                        >
                          <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                      </>
                    )}

                    <div className={styles.volumeControl}>
                      <button className={styles.controlButton} onClick={toggleMute}>
                        <FontAwesomeIcon icon={isMuted || volume === 0 ? faVolumeMute : faVolumeUp} />
                      </button>
                      
                      <div className={styles.volumeSlider}>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={isMuted ? 0 : volume}
                          onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                        />
                      </div>
                    </div>

                    <div className={styles.timeDisplay}>
                      <span>{formatTime(currentTime)} / {formatTime(duration)}</span>
                    </div>
                  </div>

                  <div className={styles.controlsRight}>
                    <button className={styles.controlButton} onClick={toggleFullscreen}>
                      <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className={styles.noVideo}>
              <div className={styles.noVideoIcon}>🎬</div>
              <h3>Видео недоступно</h3>
              <p>{isMovie ? 'Для этого фильма пока нет видео' : 'Для этого эпизода пока нет видео'}</p>
            </div>
          )}
        </div>

        {!isFullscreen && isSeries && (
          <div className={styles.episodesSection}>
            <div className={styles.episodesHeader}>
              <h3>{movie?.name || 'Эпизоды'}</h3>
              <span className={styles.episodeCount}>{episodes.length} эпизодов</span>
            </div>

            <div className={styles.episodesList}>
              {Object.keys(episodesBySeason).sort((a, b) => a - b).map(seasonNum => (
                <div key={seasonNum} className={styles.seasonGroup}>
                  <h4 className={styles.seasonTitle}>Сезон {seasonNum}</h4>
                  <div className={styles.seasonEpisodes}>
                    {episodesBySeason[seasonNum].map((episode) => (
                      <div
                        key={episode.id}
                        className={`${styles.episodeItem} ${
                          currentEpisode?.id === episode.id ? styles.active : ''
                        }`}
                        onClick={() => handleEpisodeSelect(episode)}
                      >
                        <div className={styles.episodeNumber}>
                          {episode.episode_number}
                        </div>
                        
                        {episode.thumbnail && (
                          <div className={styles.episodeThumbnail}>
                            <ImageWithFallback
                              src={episode.thumbnail}
                              alt={`Эпизод ${episode.episode_number}`}
                              fallbackIcon="🎬"
                            />
                          </div>
                        )}
                        
                        <div className={styles.episodeInfo}>
                          <div className={styles.episodeName}>
                            {episode.name || `Эпизод ${episode.episode_number}`}
                          </div>
                          {episode.duration_min && (
                            <div className={styles.episodeDuration}>
                              ⏱ {episode.duration_min} мин
                            </div>
                          )}
                        </div>

                        {currentEpisode?.id === episode.id && (
                          <div className={styles.playingIndicator}>▶</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default MoviePlayerModal
